import { create } from 'zustand';

import { getAppleOffer, type IAppleOffer } from '@/lib/api/apple-billing.client';

/**
 * - `idle`: nobody asked. Everywhere but the iOS app the store stays here and
 *   the App-Store screen stays closed.
 * - `loading`: the first answer is on its way.
 * - `ready`: `offer` holds the answer; a refresh keeps it on screen meanwhile.
 * - `failed`: the server could not be asked.
 */
type OfferStatus = 'idle' | 'loading' | 'ready' | 'failed';

interface AppStoreOfferState {
  offer: IAppleOffer | null;
  status: OfferStatus;
  /** Bumped by `reset`, so an answer for the previous workspace is dropped. */
  generation: number;
  /** Asks the server; a call while one is on its way joins it. */
  load: () => Promise<void>;
  /** Asks again after a purchase, but only where the offer was asked for at all. */
  refresh: () => Promise<void>;
  reset: () => void;
}

let inFlight: Promise<void> | null = null;

/**
 * What the App-Store screen of the iOS app may offer this workspace (KAN-10):
 * whether a purchase may start, the products and a subscription to manage.
 * One answer for the settings entry, the route, the block screen and the
 * screen itself, loaded by AppStoreSession for the signed-in workspace.
 */
export const useAppStoreOfferStore = create<AppStoreOfferState>((set, get) => ({
  offer: null,
  status: 'idle',
  generation: 0,

  load: () => {
    if (inFlight) return inFlight;
    const { generation, offer } = get();
    set({ status: offer ? 'ready' : 'loading' });
    const current = () => get().generation === generation;
    const request = getAppleOffer().then(
      (answer) => {
        if (current()) set({ offer: answer, status: 'ready' });
      },
      () => {
        if (current()) set({ status: get().offer ? 'ready' : 'failed' });
      }
    );
    inFlight = request;
    void request.finally(() => {
      if (inFlight === request) inFlight = null;
    });
    return request;
  },

  refresh: async () => {
    if (get().status !== 'idle') await get().load();
  },

  reset: () => {
    inFlight = null;
    set((state) => ({ offer: null, status: 'idle', generation: state.generation + 1 }));
  },
}));
